// Favorite Teachers Service
// Handles favoriting/unfavoriting teachers and fetching favorites

import { supabase } from './supabaseClient';

/**
 * Get all favorite teachers for the current student
 * @returns {Promise} Array of teacher UUIDs
 */
export async function getFavoriteTeachers() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: [], error: 'Not authenticated' };
    }

    const { data, error } = await supabase
      .from('favorite_teachers')
      .select('teacher_uuid')
      .eq('student_id', user.id);

    if (error) {
      console.error('Error fetching favorites:', error);
      return { data: [], error: error.message };
    }

    // Return array of teacher UUIDs
    const teacherIds = (data || []).map(fav => fav.teacher_uuid);
    return { data: teacherIds, error: null };
  } catch (error) {
    console.error('Error in getFavoriteTeachers:', error);
    return { data: [], error: error.message };
  }
}

/**
 * Add a teacher to favorites
 * @param {string} teacherUuid - UUID of the teacher to favorite
 * @returns {Promise} Success/error response
 */
export async function addFavoriteTeacher(teacherUuid) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { data, error } = await supabase
      .from('favorite_teachers')
      .insert([{
        student_id: user.id,
        teacher_uuid: teacherUuid
      }])
      .select();

    if (error) {
      console.error('Error adding favorite:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in addFavoriteTeacher:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Remove a teacher from favorites
 * @param {string} teacherUuid - UUID of the teacher to unfavorite
 * @returns {Promise} Success/error response
 */
export async function removeFavoriteTeacher(teacherUuid) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { error } = await supabase
      .from('favorite_teachers')
      .delete()
      .eq('student_id', user.id)
      .eq('teacher_uuid', teacherUuid);

    if (error) {
      console.error('Error removing favorite:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in removeFavoriteTeacher:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Check if a teacher is favorited
 * @param {string} teacherUuid - UUID of the teacher
 * @returns {Promise} Boolean indicating if teacher is favorited
 */
export async function isTeacherFavorited(teacherUuid) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { isFavorited: false, error: 'Not authenticated' };
    }

    const { data, error } = await supabase
      .from('favorite_teachers')
      .select('id')
      .eq('student_id', user.id)
      .eq('teacher_uuid', teacherUuid)
      .maybeSingle();

    if (error) {
      console.error('Error checking favorite status:', error);
      return { isFavorited: false, error: error.message };
    }

    return { isFavorited: !!data, error: null };
  } catch (error) {
    console.error('Error in isTeacherFavorited:', error);
    return { isFavorited: false, error: error.message };
  }
}

/**
 * Toggle favorite status for a teacher
 * @param {string} teacherUuid - UUID of the teacher
 * @returns {Promise} New favorite status and success/error response
 */
export async function toggleFavoriteTeacher(teacherUuid) {
  try {
    const { isFavorited } = await isTeacherFavorited(teacherUuid);

    if (isFavorited) {
      const result = await removeFavoriteTeacher(teacherUuid);
      return { ...result, isFavorited: false };
    } else {
      const result = await addFavoriteTeacher(teacherUuid);
      return { ...result, isFavorited: true };
    }
  } catch (error) {
    console.error('Error in toggleFavoriteTeacher:', error);
    return { success: false, error: error.message };
  }
}
